import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";

export const useAntiCheat = ({ active = false, isSubmitted = false, maxWarnings = 3, onMaxWarningsReached = () => {} }) => {
  const [warningCount, setWarningCount] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (isSubmitted) {
      submittingRef.current = false;
    }
  }, [isSubmitted]);

  const displayWarning = (message) => {
    if (!active || isSubmitted || submittingRef.current) return;

    const newWarningCount = warningCount + 1;
    setWarningCount(newWarningCount);

    toast.warn(
      <div className="warning-toast">
        <p>
          <strong>{message}</strong>
        </p>
        <p>
          Warning {newWarningCount} of {maxWarnings}
        </p>
      </div>
    );

    if (newWarningCount >= maxWarnings) {
      submittingRef.current = true;

      onMaxWarningsReached();
    }
  };

  const enableFullscreen = () => {
    try {
      const element = document.documentElement;
      element.requestFullscreen?.() || element.mozRequestFullScreen?.() || element.webkitRequestFullscreen?.() || element.msRequestFullscreen?.();

      setIsFullscreen(true);
    } catch (error) {
      console.error("Enter fullscreen failed:", error);
      toast.warning("You can continue the exam, but for best experience, please switch to fullscreen.");
    }
  };

  const exitFullscreen = () => {
    try {
      const isActive = document.visibilityState === "visible";
      const isFullscreen =
        document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement || document.msFullscreenElement;

      if (isActive && isFullscreen) {
        document.exitFullscreen?.() || document.mozCancelFullScreen?.() || document.webkitExitFullscreen?.() || document.msExitFullscreen?.();

        setIsFullscreen(false);
      }
    } catch (error) {
      console.error("Exit fullscreen failed:", error);
    }
  };

  const isActiveMonitoring = active && !isSubmitted && !submittingRef.current;

  useEffect(() => {
    if (!isActiveMonitoring) return;

    const handleFullscreenChange = () => {
      const isCurrentFS = Boolean(document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement || document.msFullscreenElement);

      setIsFullscreen(isCurrentFS);
      if (!isCurrentFS && !submittingRef.current) {
        displayWarning("Please return to fullscreen mode to continue the exam.");
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && !submittingRef.current) {
        displayWarning("Tab switching detected! Please remain on the exam page.");
      }
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      if (!submittingRef.current) {
        displayWarning("Right-clicking is not allowed");
      }
    };

    const handleCopy = (e) => {
      e.preventDefault();
      if (!submittingRef.current) {
        displayWarning("Copying is not allowed");
      }
    };

    const handleCut = (e) => {
      e.preventDefault();
      if (!submittingRef.current) {
        displayWarning("Cutting is not allowed");
      }
    };

    const handlePaste = (e) => {
      e.preventDefault();
      if (!submittingRef.current) {
        displayWarning("Pasting is not allowed");
      }
    };

    const handleKeyboardShortcuts = (e) => {
      const combo = `${e.ctrlKey ? "Ctrl+" : ""}${e.shiftKey ? "Shift+" : ""}${e.altKey ? "Alt+" : ""}${e.key}`;
      const blockedKeys = ["F5", "Ctrl+r", "Ctrl+c", "F12", "Ctrl+Shift+i","Ctrl+Shift+j", "Alt+Tab"];
      if (blockedKeys.includes(combo)) {
        e.preventDefault();
        if (!submittingRef.current) {
          displayWarning("Restricted keyboard shortcut detected");
        }
      }
    };

    const handleMouseLeave = () => {
      if (isFullscreen && !submittingRef.current) {
        displayWarning("Keep your cursor within the exam window!");
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("copy", handleCopy);
    document.addEventListener("cut", handleCut);
    document.addEventListener("paste", handlePaste);
    document.addEventListener("keydown", handleKeyboardShortcuts);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("copy", handleCopy);
      document.removeEventListener("cut", handleCut);
      document.removeEventListener("paste", handlePaste);
      document.removeEventListener("keydown", handleKeyboardShortcuts);
    };
  }, [isActiveMonitoring, isFullscreen, warningCount, maxWarnings]);

  const resetAntiCheat = () => {
    setWarningCount(0);
    setIsFullscreen(false);
    submittingRef.current = false;
  };

  return {
    warningCount,
    isFullscreen,
    displayWarning,
    enableFullscreen,
    exitFullscreen,
    resetAntiCheat,
  };
};